/**
 * REVA valuation lifecycle state — mirrors the backend enum
 * `de.immobrain.reva.persistence.document.RevaValuationLifeCycleState`.
 *
 * Shared between client and reva-pdf so both sides use the same set of state names, together with the
 * ordinal ranking, comparison and label helpers built on top of it.
 */
export const RevaLifeCycleState = {
    New: "New",
    DataInput: "DataInput",
    PreFinal: "PreFinal",
    Final: "Final",
    Correction: "Correction",
} as const;

// Value and type intentionally share the name (a valid TS value/type merge);
// the lint rule doesn't model this and would flag it as a redeclaration.
export type RevaLifeCycleState = (typeof RevaLifeCycleState)[keyof typeof RevaLifeCycleState];

/**
 * All lifecycle states, ordered by ascending ordinal rank — the order IS the rank used for
 * "at least" comparisons:
 *
 *   New (0) < DataInput (1) < PreFinal (2) < Final (3) < Correction (4)
 *
 * Note on `Correction`: a valuation in correction has already passed through `Final` at least once
 * (it can only be reached by reopening a finalized valuation), so it intentionally ranks **above**
 * `Final`. This is the "maturity reached" axis — use {@link isLifeCycleAtLeast} for it.
 *
 * The orthogonal "is currently locked / read-only" question is NOT ordinal: only `Final` locks
 * editing (`Correction` is editable again), and loaded snapshots are read-only too. That decision
 * lives centrally in the client's `selectReadOnly` (true for a loaded snapshot or
 * `lifeCycleState === "Final"`) and will later also depend on the user's permissions.
 */
export const LIFE_CYCLE_STATES: readonly RevaLifeCycleState[] = [
    RevaLifeCycleState.New,
    RevaLifeCycleState.DataInput,
    RevaLifeCycleState.PreFinal,
    RevaLifeCycleState.Final,
    RevaLifeCycleState.Correction,
];

/**
 * Precomputed rank lookup (state → its index in {@link LIFE_CYCLE_STATES}),
 * built once at module load so {@link lifeCycleRank} is an O(1) map lookup
 * instead of a linear `indexOf` scan on every call.
 */
const LIFE_CYCLE_RANK = Object.fromEntries(LIFE_CYCLE_STATES.map((state, index) => [state, index] as const)) as Record<
    RevaLifeCycleState,
    number
>;

/**
 * Ordinal rank of a lifecycle state (its index in {@link LIFE_CYCLE_STATES}).
 * Unknown / null / undefined states return `-1`.
 * @param state lifecycle state
 */
export function lifeCycleRank(state: RevaLifeCycleState | null | undefined): number {
    return state == null ? -1 : (LIFE_CYCLE_RANK[state] ?? -1);
}

/**
 * Ordinal "at least" comparison: `true` if `state` has reached at least `min`.
 *
 * Because `Correction` ranks above `Final`, a valuation in correction counts
 * as `>= PreFinal` and `>= Final`. Returns `false` if either argument is an
 * unknown / null state.
 *
 * @param state the valuation's current lifecycle state
 * @param min   the minimum required state
 */
export function isLifeCycleAtLeast(state: RevaLifeCycleState | null | undefined, min: RevaLifeCycleState): boolean {
    const s = lifeCycleRank(state);
    const m = lifeCycleRank(min);
    return s >= 0 && m >= 0 && s >= m;
}

/** i18n key for the label of a lifecycle state (e.g. `lifeCycleState.PreFinal`). */
export function lifeCycleStateLabelKey(state: RevaLifeCycleState | null | undefined): string {
    return state == null ? "lifeCycleState.unknown" : `lifeCycleState.${state}`;
}

/**
 * Kind of live (editable) data entry that can be resumed when leaving a snapshot view.
 * `"dataEntry"` = normal data entry (before/at PreFinal), `"correction"` = ongoing correction.
 */
export type LiveDataEntryKind = "dataEntry" | "correction";

/**
 * Decide whether the current user may load the live data state (i.e. leave a snapshot view to
 * continue working) and which kind of data entry it is, based on the live lifecycle state and
 * the user's permissions:
 *
 *  - before PreFinal (New / DataInput): always allowed — normal data entry, no extra right;
 *  - PreFinal: allowed only with the finalization permission — normal data entry;
 *  - Correction: allowed only with the finalization permission — correction data entry;
 *  - Final: not offered (the live Final state is not loaded; a correction is started instead).
 *
 * Returns `null` when loading the live data state is not available to the user. An explicit
 * switch is used because `Correction` ranks above `Final` (so an ordinal comparison won't do).
 *
 * @param lifeCycleState the valuation's current (live) lifecycle state
 * @param permissions whether the user may finalize
 */
export function liveDataEntryKind(
    lifeCycleState: RevaLifeCycleState | null | undefined,
    permissions: {canFinalize: boolean},
): LiveDataEntryKind | null {
    switch (lifeCycleState) {
        case RevaLifeCycleState.Correction:
            return permissions.canFinalize ? "correction" : null;
        case RevaLifeCycleState.PreFinal:
            return permissions.canFinalize ? "dataEntry" : null;
        case RevaLifeCycleState.New:
        case RevaLifeCycleState.DataInput:
            return "dataEntry";
        default:
            // Final or unknown: the live data state is not offered here.
            return null;
    }
}
